import DPO from 'dpo8core';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Formik, Form, Field } from 'formik';
import LocalizedText from '@LocalizedText';
import { FORGOT_PASSWORD_FORM, SIGNUP_FORM } from '@app/constants';
import { inject } from '@app/providers/QAU.ContextProvider';
import useIntlMessage from '@app/hooks/use-intl-message';

import './LoginForm.scss';

const LoginForm = ({ selectLoginSignupStep, onSignIn, signInError }) => {
  const [showPasswordField, setShowPasswordField] = useState(false);
  const invalidEmailError = useIntlMessage('qau-invalid-email-error');
  const goToForgotPasswordForm = () => selectLoginSignupStep(FORGOT_PASSWORD_FORM);
  const goToSignupForm = () => selectLoginSignupStep(SIGNUP_FORM);

  const validateEmail = email => {
    if (!email) return undefined;
    return DPO.project.Validator.email(email) && invalidEmailError;
  };

  const onKeyDown = keyEvent => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      if (!showPasswordField) {
        keyEvent.preventDefault();
        if (keyEvent.target.className === 'valid') {
          setShowPasswordField(true);
        }
      }
    }
  };

  return (
    <div id="login-form">
      <div className="login-form-title">Sign In</div>
      <Formik
        initialValues={{
          email: '',
          password: '',
        }}
        validateOnChange="true"
        onSubmit={onSignIn}
      >
        {({ values, errors, touched, isValid, dirty }) => (
          <Form onKeyDown={onKeyDown}>
            <label htmlFor="login-email">
              <LocalizedText id="qau-email" tagName="span" />
              <Field
                type="email"
                name="email"
                id="login-email"
                validate={validateEmail}
                maxLength="100"
                className={classnames({ invalid: errors.email && touched.email }, { valid: !errors.email && values.email.length > 1 })}
              />
              <span className="email-icons" />
              {errors.email && touched.email ? <div className="error-msg">{errors.email}</div> : null}
            </label>
            {showPasswordField ? (
              <>
                <label htmlFor="login-password" className="required">
                  <LocalizedText id="qau-password" tagName="span" />
                  <Field
                    type="password"
                    name="password"
                    id="login-password"
                    maxLength="100"
                    className={classnames({ invalid: signInError })}
                  />
                  <span className="password-icons" />
                  {signInError ? <div className="error-msg">{signInError}</div> : null}
                </label>
                <button className="link-button forgot-password" type="button" onClick={goToForgotPasswordForm}>
                  <LocalizedText id="qau-forgot-password" />
                </button>
                <button className="submit-btn" type="submit" disabled={!isValid || !showPasswordField}>
                  <LocalizedText id="qau-sign-in-btn" />
                </button>
              </>
            ) : (
              <>
                <button
                  className="next-btn"
                  type="button"
                  disabled={!isValid || !dirty}
                  onClick={() => {
                    setShowPasswordField(true);
                  }}
                >
                  Next
                </button>
                <div className="create-account-link">
                  <LocalizedText id="qau-create-account-link-text" />
                  <button className="link-button" type="button" onClick={goToSignupForm}>
                    <LocalizedText id="qau-create-account-link" />
                  </button>
                </div>
              </>
            )}
          </Form>
        )}
      </Formik>
      <div className="terms-and-privacy">
        <LocalizedText id="qau-terms-and-privacy" />
      </div>
    </div>
  );
};

LoginForm.propTypes = {
  selectLoginSignupStep: PropTypes.func.isRequired,
  onSignIn: PropTypes.func.isRequired,
  signInError: PropTypes.string,
};

export default inject('flowConfig')(LoginForm);
